// Solutions (02-DESIGN/14-solutions/01-the-manifest.md § the console:
// Solutions; 03-the-console.md § where a solution appears; 01-the-console.md
// § conventions: a table): what is installed, one row per solution with its
// version label and its state, and for an admin what is available from the
// catalogue the workspace may see, in the same table marked *available*,
// each opening its declaration with *Install*. A member sees what is
// installed and opens its panels; installing is an admin's. A licensed
// entry says *requires a license*; an installed licensed solution says
// *licensed until …*, and past its expiry the row is marked *license
// expired* (02-DESIGN/17-plans/03-solution-licenses.md § the record, the
// page and the end).

import { tenantPath } from "../../address/address.ts";
import { Empty, NotAllowed, PageHead, Pending, Pill, useLoad, type Tone } from "../../ui/components.tsx";
import { Table, type Column } from "../../ui/table.tsx";
import { SERVICE, Subjects, type CatalogueListReply } from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";
import { readInstalled } from "./installed.ts";
import { isExpired, licenseWords, REQUIRES } from "./license.ts";
import { stateWords } from "./words.ts";

/** Listed is one row: an installed solution, or one the catalogue offers. */
interface Listed {
  name: string;
  display: string;
  sub: string;
  state: { word: string; tone: Tone };
}

const columns: Column<Listed>[] = [
  {
    key: "solution",
    header: "Solution",
    width: "minmax(0, 2fr)",
    value: (r) => r.display,
    text: (r) => `${r.display} ${r.sub}`,
    cell: (r) => (
      <span className="cell">
        <span className="cell-main">{r.display}</span>
        <span className="cell-sub">{r.sub}</span>
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    width: "140px",
    facet: "Status",
    value: (r) => r.state.word,
    cell: (r) => <Pill tone={r.state.tone}>{r.state.word}</Pill>,
  },
];

export function SolutionsPage() {
  const s = useSurface();
  const admin = s.offered("solutions:manage");
  const [installed] = useLoad(`solutions/installed/${s.tenant}`, () => readInstalled(s.bus));
  const [catalogue] = useLoad(`solutions/catalogue/${s.tenant}/${String(admin)}`, () =>
    admin
      ? s.bus.call<CatalogueListReply>(SERVICE, Subjects.catalogueList(), undefined)
      : Promise.resolve<CatalogueListReply>({ solutions: [] }),
  );
  const names = new Set(installed.state === "ready" ? installed.value.map((r) => r.name) : []);
  const available =
    catalogue.state === "ready"
      ? catalogue.value.solutions.filter((e) => !names.has(e.latest.name) && e.withdrawn !== true)
      : [];
  const rows: Listed[] = [
    ...(installed.state === "ready"
      ? installed.value.map((r) => ({
          name: r.name,
          display: r.manifest?.display ?? r.name,
          sub: [
            r.manifest?.version !== undefined && r.manifest.version !== ""
              ? `version ${r.manifest.version}`
              : `release ${String(r.revision)}`,
            licenseWords(r.license),
          ]
            .filter((x) => x !== undefined)
            .join(" · "),
          state: isExpired(r.license)
            ? { word: "license expired", tone: "bad" as const }
            : stateWords(r, undefined),
        }))
      : []),
    ...available.map((e) => ({
      name: e.latest.name,
      display: e.latest.manifest.display,
      sub: [
        e.latest.manifest.description ??
          `from ${e.latest.publisher === "platform" ? "Impire" : e.latest.publisher}`,
        e.licensed === true ? REQUIRES : undefined,
      ]
        .filter((x) => x !== undefined)
        .join(" · "),
      state: { word: "available", tone: "neutral" as const },
    })),
  ];
  return (
    <>
      <PageHead
        title="Solutions"
        line={`What ${s.name} has installed, and what it can. A solution runs inside ${s.name} and adds its own pages here.`}
      />
      {installed.state === "pending" && <Pending what="Reading what is installed" />}
      {installed.state === "refused" && <NotAllowed err={installed.err} what="Installed solutions" />}
      {admin && catalogue.state === "pending" && <Pending what="Reading the catalogue" />}
      {admin && catalogue.state === "refused" && <NotAllowed err={catalogue.err} what="The catalogue" />}
      {installed.state === "ready" && rows.length === 0 && (admin ? catalogue.state === "ready" : true) && (
        <Empty>
          Nothing is installed yet.{" "}
          {admin ? "Nothing else is available to install." : "An admin installs solutions here."}
        </Empty>
      )}
      {rows.length > 0 && (
        <Table
          label="Solutions"
          rows={rows}
          rowKey={(r) => r.name}
          sort={{ key: "solution", dir: "asc" }}
          to={(r) => tenantPath(s.tenant, "solutions", r.name)}
          columns={columns}
        />
      )}
    </>
  );
}
